import { useState } from 'react';
import { Icon } from '@iconify/react';
import { Brain, Users, Shield, Video, Play, Database, Workflow } from 'lucide-react';
import IntegrationLandingBase from '@/components/integrations/IntegrationLandingBase';

export default function MSTeamsLanding() {
  const [copiedCode, setCopiedCode] = useState(false);

  const handleCopySetupCode = async () => {
    const setupCode = `# 1. Install Memara App in Teams
Search "Memara" in Teams App Store

# 2. Connect to your workspace
@Memara setup workspace

# 3. Start capturing memories
@Memara remember this meeting summary

# 4. Search team knowledge
@Memara search "quarterly planning"`;
    try {
      await navigator.clipboard.writeText(setupCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (error) {
      const textArea = document.createElement('textarea');
      textArea.value = setupCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const handleGetStarted = () => {
    window.location.href = '/login';
  };

  const handleSeeDemo = () => {
    window.open('https://www.youtube.com/watch?v=dQw4w9WgXcQ', '_blank');
  };

  return (
    <IntegrationLandingBase
      registryIntegrationId="microsoft_teams"
      icon={
        <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 shadow" aria-label="Microsoft Teams Logo">
          <Icon icon="logos:microsoft-teams" width={48} height={48} />
        </span>
      }
      title="Microsoft Teams Integration"
      description="Enterprise-grade memory for Microsoft 365 ecosystems. Capture meeting insights, project discussions, and organizational knowledge with seamless Microsoft 365 integration, Azure AD authentication, and enterprise compliance for distributed team collaboration."
      features={[
        { icon: <Video className="h-4 w-4 text-blue-600" />, title: 'Intelligent Meeting Capture', description: 'Extract key insights from Teams meetings, calls, and video conferences with AI-powered transcription and context analysis.' },
        { icon: <Brain className="h-4 w-4 text-blue-600" />, title: 'Microsoft 365 AI Integration', description: 'Deep integration with Microsoft AI services for enhanced workplace intelligence and organizational context understanding.' },
        { icon: <Users className="h-4 w-4 text-blue-600" />, title: 'Enterprise Team Collaboration', description: 'Shared organizational memory with Azure AD integration, role-based permissions, and enterprise directory synchronization.' },
        { icon: <Shield className="h-4 w-4 text-blue-600" />, title: 'Microsoft Security Standards', description: 'Enterprise-grade security with Microsoft compliance frameworks, data residency controls, and advanced threat protection.' },
        { icon: <Database className="h-4 w-4 text-blue-600" />, title: 'Cross-Application Memory', description: 'Unified memory capture across Teams, SharePoint, OneDrive, and Microsoft 365 applications with intelligent content correlation.' },
        { icon: <Workflow className="h-4 w-4 text-blue-600" />, title: 'Power Platform Integration', description: 'Seamless integration with Power Automate, Power BI, and Power Apps for enterprise workflow automation and business intelligence.' },
      ]}
      setupSteps={[
        'Install Memara App from Microsoft Teams App Store',
        'Connect to your Microsoft 365 workspace with Azure AD',
        'Configure enterprise permissions and compliance settings',
        'Start capturing organizational knowledge across Microsoft 365',
      ]}
      ctas={[
        {
          label: '🚀 Get Started Free',
          onClick: handleGetStarted,
          className: 'bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 h-11 rounded-md text-base font-semibold',
        },
        {
          label: 'See Demo',
          onClick: handleSeeDemo,
          className: 'border-blue-200 text-blue-600 hover:bg-blue-50 px-8 py-4 h-11 rounded-md text-base border',
          icon: <Play className="w-4 h-4 mr-2" />,
        },
        {
          label: copiedCode ? 'Copied!' : 'Copy Setup Code',
          onClick: handleCopySetupCode,
          className: 'border-blue-200 text-blue-600 hover:bg-blue-50 px-8 py-4 h-11 rounded-md text-base border',
          icon: copiedCode ? <Brain className="w-4 h-4 mr-2" /> : <Icon icon="logos:microsoft-teams" width={20} height={20} />,
        },
      ]}
      videoUrl="https://www.youtube.com/watch?v=dQw4w9WgXcQ"
      microcopy="Trusted by enterprise organizations, Fortune 500 companies, and Microsoft 365 administrators using Teams for mission-critical business collaboration and organizational knowledge management."
      trustedBy="Microsoft, Enterprise Organizations, and more."
      testimonial="Memara's Teams integration transforms our Microsoft 365 environment into an intelligent organizational memory system that captures every important meeting insight, project discussion, and business decision with enterprise-grade security and compliance."
    />
  );
}